import { PIPELINE_PROGRESS_STAGE_LABELS } from '../../features/pipelineStatus/pipelineProgressCopy';
import {
	activityDisplayNames,
	activityHighLevelStep,
	activityStageBindingsFromDefinitions,
	normalizeActivityPipeline,
} from '../../features/pipelineStatus/pipelineActivityHighLevelStep';

// Canonical PortalApi / SNS `PipelineStageStatus` enum names. The previous values
// ('Executing', 'Failure', 'Success') predated the contract rename and matched nothing.
const INTERESTING_STATUSES = new Set(['Started', 'Failed', 'Completed']);
export const ACTIVITY_STRIP_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** @param {unknown} value */
export function isPipelineStatusGuid(value) {
	return GUID_RE.test(String(value ?? '').trim());
}

/** @param {string | undefined} iso */
function occurredTime(iso) {
	if (!iso) return 0;
	const t = new Date(iso).getTime();
	return Number.isNaN(t) ? 0 : t;
}

/**
 * @param {Record<string, unknown>} row
 */
function activityDedupeKey(row) {
	const pipeline = String(row.pipeline ?? '');
	const sid = String(row.subjectId ?? '');
	const iteration = String(row.iterationValue ?? '');
	return `${pipeline}|${sid}|${iteration}`;
}

/**
 * @param {Record<string, unknown>} row
 */
function nodeIterationKey(row) {
	const service = String(row.service ?? '').trim();
	const stage = String(row.stage ?? '').trim();
	const iteration = String(row.iterationValue ?? '').trim();
	return `${service}|${stage}|${iteration}`;
}

/**
 * @param {Record<string, unknown>[]} rows
 */
function latestRow(rows) {
	return rows.reduce((best, cur) => (
		occurredTime(cur.occurredAt) >= occurredTime(best.occurredAt) ? cur : best
	));
}

/**
 * Latest fact for each producer step. A later fact on the same step replaces an earlier one.
 * @param {Record<string, unknown>[]} rows
 * @returns {Map<string, Record<string, unknown>>}
 */
function latestByNodeIteration(rows) {
	/** @type {Map<string, Record<string, unknown>>} */
	const map = new Map();
	for (const row of rows) {
		const key = nodeIterationKey(row);
		const prev = map.get(key);
		if (!prev || occurredTime(row.occurredAt) >= occurredTime(prev.occurredAt)) {
			map.set(key, row);
		}
	}
	return map;
}

/**
 * @param {Record<string, unknown>} row
 */
function activitySubjectKey(row) {
	return `${normalizeActivityPipeline(row.pipeline)}|${String(row.subjectId ?? '')}`;
}

/**
 * @param {import('../features/pipelineStatus/pipelineStatusTypes').PipelineStatusGraphDefinition[]} definitions
 * @param {unknown} pipeline
 */
function graphForPipeline(definitions, pipeline) {
	const name = normalizeActivityPipeline(pipeline);
	return definitions.find((def) =>
		(def?.stages ?? []).some(
			(stage) => normalizeActivityPipeline(stage?.factMatch?.pipeline) === name,
		),
	) ?? null;
}

/**
 * @param {Map<string, Record<string, unknown>>} latest
 * @param {import('../features/pipelineStatus/pipelineStatusTypes').PipelineStatusGraphStage} stage
 */
function rowsForGraphStage(latest, stage) {
	const service = String(stage?.factMatch?.service ?? '').trim();
	const producerStage = String(stage?.factMatch?.stage ?? '').trim();
	const prefix = `${service}|${producerStage}|`;
	const rows = [];
	for (const [key, row] of latest) {
		if (key.startsWith(prefix)) rows.push(row);
	}
	return rows;
}

/**
 * @param {string} status
 */
function isTerminalSuccess(status) {
	return status === 'Completed' || status === 'Skipped';
}

/**
 * Ready when every graph step has a fact and each step's latest fact succeeded,
 * including every sample set that appears on any step.
 * @param {Record<string, unknown>[]} rows
 * @param {import('../features/pipelineStatus/pipelineStatusTypes').PipelineStatusGraphDefinition | null} definition
 */
function isActivitySubjectReady(rows, definition) {
	if (!definition?.stages?.length || rows.length === 0) return false;
	const latest = latestByNodeIteration(rows);
	/** @type {Set<string>} */
	const iterations = new Set();
	for (const stage of definition.stages) {
		const stageRows = rowsForGraphStage(latest, stage);
		if (stageRows.length === 0) return false;
		for (const row of stageRows) {
			if (!isTerminalSuccess(String(row.status || ''))) return false;
			const iteration = String(row.iterationValue ?? '').trim();
			if (iteration) iterations.add(iteration);
		}
	}
	if (iterations.size === 0) return true;
	for (const stage of definition.stages) {
		const stageIterations = rowsForGraphStage(latest, stage)
			.map((row) => String(row.iterationValue ?? '').trim())
			.filter(Boolean);
		if (stageIterations.length === 0) continue;
		if (stageIterations.length !== iterations.size) return false;
		for (const iteration of iterations) {
			if (!stageIterations.includes(iteration)) return false;
		}
	}
	return true;
}

/**
 * Statuses that can be the latest fact on a graph step. `Skipped` is a terminal success
 * for readiness, and it is not a ticker line on its own.
 * @param {string} status
 */
function isReadinessStatus(status) {
	return INTERESTING_STATUSES.has(status) || isTerminalSuccess(status);
}

/**
 * Filter to graph-handled facts from the last six hours, then keep
 * one fact per project or sample set (pipeline + subject + iteration).
 * An outstanding failure is shown instead of a newer fact on another step.
 * A subject whose graph steps have all succeeded, including skipped steps, is marked ready.
 * That ready row is the latest fact in the group, including a skip that finished the subject.
 * A row the activity route already marked `activityTone: 'ready'` is shown as that line
 * and replaces any other line for the same subject.
 * Optional `visibleMax` caps the list (ticker shows all).
 * @param {Array<Record<string, unknown>>} rows
 * @param {import('../features/pipelineStatus/pipelineStatusTypes').PipelineStatusGraphDefinition[]} definitions
 * @param {number} [visibleMax]
 * @param {number} [nowMs]
 */
export function processActivityStripRows(rows, definitions, visibleMax, nowMs = Date.now()) {
	if (!Array.isArray(rows) || rows.length === 0) return [];
	if (!Array.isArray(definitions) || definitions.length === 0) return [];

	const bindings = activityStageBindingsFromDefinitions(definitions);

	const cutoff = nowMs - ACTIVITY_STRIP_MAX_AGE_MS;
	const routeReady = rows.filter((r) =>
		r && r.activityTone === 'ready' && occurredTime(r.occurredAt) >= cutoff);
	const routeReadySubjects = new Set(routeReady.map((r) => activitySubjectKey(r)));
	const incoming = rows.filter((r) => r?.activityTone !== 'ready');
	const considered = incoming.filter((r) => {
		if (!r || !isReadinessStatus(String(r.status || ''))) return false;
		if (!activityHighLevelStep(r, bindings)) return false;
		return occurredTime(r.occurredAt) >= cutoff;
	});

	/** @type {Map<string, Record<string, unknown>[]>} */
	const byKey = new Map();
	/** @type {Map<string, Record<string, unknown>[]>} */
	const bySubject = new Map();
	for (const r of considered) {
		const key = activityDedupeKey(r);
		const group = byKey.get(key);
		if (group) group.push(r);
		else byKey.set(key, [r]);

		const subject = activitySubjectKey(r);
		const subjectRows = bySubject.get(subject);
		if (subjectRows) subjectRows.push(r);
		else bySubject.set(subject, [r]);
	}

	/** @type {Set<string>} */
	const readySubjects = new Set();
	for (const [subject, subjectRows] of bySubject) {
		const definition = graphForPipeline(definitions, subjectRows[0]?.pipeline);
		if (isActivitySubjectReady(subjectRows, definition)) readySubjects.add(subject);
	}

	const selected = [];
	for (const group of byKey.values()) {
		const nodeLatests = Array.from(latestByNodeIteration(group).values());
		const failures = nodeLatests.filter((r) => String(r.status || '') === 'Failed');
		const currentDisplay = nodeLatests.filter((r) => INTERESTING_STATUSES.has(String(r.status || '')));
		const subjectKey = activitySubjectKey(group[0]);
		if (failures.length === 0 && readySubjects.has(subjectKey)) continue;
		if (currentDisplay.length === 0) continue;
		const chosen = latestRow(failures.length > 0 ? failures : currentDisplay);
		if (failures.length > 0) {
			selected.push({ ...chosen, activityTone: 'failed' });
		} else {
			selected.push(chosen);
		}
	}
	for (const subject of readySubjects) {
		const subjectRows = bySubject.get(subject);
		if (!subjectRows?.length) continue;
		let activityProjectCode;
		for (const subjectRow of subjectRows) {
			activityProjectCode = activityDisplayNames(subjectRow).projectCode;
			if (activityProjectCode) break;
		}
		selected.push({ ...latestRow(subjectRows), activityTone: 'ready', activityProjectCode });
	}

	const withoutRoute = selected.filter((r) => !routeReadySubjects.has(activitySubjectKey(r)));
	const sorted = [...routeReady, ...withoutRoute].sort((a, b) => occurredTime(b.occurredAt) - occurredTime(a.occurredAt));
	if (visibleMax == null) return sorted;
	return sorted.slice(0, visibleMax);
}

/**
 * @param {Record<string, unknown>} row
 */
function activityWhoLabel(row) {
	const names = activityDisplayNames(row);
	const whoParts = [];
	if (names.projectCode) whoParts.push(names.projectCode);
	if (names.sampleSetName) whoParts.push(names.sampleSetName);
	if (whoParts.length > 0) return whoParts.join(' ');
	const fallback = String(row.subjectId ?? row.SubjectId ?? '').trim();
	return fallback && !isPipelineStatusGuid(fallback) ? fallback : '';
}

/**
 * Project home for a ticker row. The activity route sets hasProjectLink when the caller
 * can open that project. ProjectContainer rewrites that id to the project code.
 * @param {Record<string, unknown>} row
 * @returns {string | null}
 */
export function activityProjectHomePath(row) {
	if (row?.hasProjectLink !== true && row?.HasProjectLink !== true) return null;
	const subjectId = String(row?.subjectId ?? row?.SubjectId ?? '').trim();
	if (!isPipelineStatusGuid(subjectId)) return null;
	return `/home/project/${subjectId}/home`;
}

/**
 * @param {Record<string, unknown>} row
 * @param {Map<string, { id: string, label: string }> | import('../features/pipelineStatus/pipelineStatusTypes').PipelineStatusGraphDefinition[]} catalog
 */
export function formatActivityStripLine(row, catalog) {
	if (row?.activityTone === 'ready') {
		const stamped = row.activityProjectCode || activityDisplayNames(row).projectCode;
		const subjectId = String(row.subjectId ?? row.SubjectId ?? '').trim();
		const who = stamped || (subjectId && !isPipelineStatusGuid(subjectId) ? subjectId : '');
		return [who, PIPELINE_PROGRESS_STAGE_LABELS.ready].filter(Boolean).join(' • ');
	}
	const who = activityWhoLabel(row);
	const step = activityHighLevelStep(row, catalog);
	const parts = [who, step?.label, row.status].filter(Boolean);
	return parts.join(' • ');
}
